import {ComponentProps, createElement, forwardRef, useEffect, useImperativeHandle, useRef} from 'react';

class ResizingInputEngine {
    private readonly canvasContext: CanvasRenderingContext2D;
    private readonly computedStyle: CSSStyleDeclaration;
    private lastSyncValue: string;

    constructor(private readonly input: HTMLInputElement) {
        this.lastSyncValue = this.input.value;
        this.canvasContext = document.createElement('canvas').getContext('2d')!;
        this.computedStyle = window.getComputedStyle(this.input);
    }

    update(forced = false) {
        const {input, canvasContext, computedStyle: cs} = this;

        if (!forced && input.value === this.lastSyncValue) return;
        this.lastSyncValue = input.value;

        canvasContext.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
        input.style.width = `${canvasContext.measureText(input.value).width}px`;
    }
}

type DefaultInputProps = ComponentProps<'input'>
type DefaultOnchange = Pick<DefaultInputProps, 'onChange'>
type ResizingInputProps = Omit<DefaultInputProps, 'value' | 'onChange'> & {value: string} & Required<DefaultOnchange>

const ResizingInput = forwardRef<HTMLInputElement, ResizingInputProps>((props, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!, [ref]);

    const engine = useRef<ResizingInputEngine>();

    useEffect(() => {
        if (!inputRef.current) return;
        engine.current = new ResizingInputEngine(inputRef.current);
        engine.current.update(true);
    }, []);

    useEffect(() => {
        engine.current?.update();
    }, [props.value]);

    return createElement('input', {ref: inputRef, style: {width: 0}, ...props});
});
ResizingInput.displayName = 'ResizingInput';

export default ResizingInput;
